import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '../../stores/authStore';
import { colors } from '../../styles/colors';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export default function SignupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const signup = useAuthStore((s) => s.signup);
  const googleSignIn = useAuthStore((s) => s.googleSignIn);
  const appleSignIn = useAuthStore((s) => s.appleSignIn);
  const loading = useAuthStore((s) => s.loading);
  const isKo = i18n.language === 'ko';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== passwordConfirm) { setError(isKo ? '비밀번호가 일치하지 않습니다.' : 'Passwords do not match.'); return; }
    if (password.length < 6) { setError(isKo ? '비밀번호는 6자 이상이어야 합니다.' : 'Password must be at least 6 characters.'); return; }
    if (!agreed || !privacyAgreed) { setError(isKo ? '모든 약관에 동의해주세요' : 'Please agree to all terms'); return; }
    try {
      await signup(email, password);
      navigate('/verify-email', { replace: true });
    } catch (err: any) {
      const msg = err.message ?? '';
      const code = (err as any).code ?? '';
      if (msg.startsWith('COOLDOWN:')) {
        const days = msg.split(':')[1];
        setError(t('auth.deletionCooldown', { days }));
      } else if (code === 'auth/email-already-in-use') {
        setError(isKo ? '이미 가입된 이메일입니다. Google 계정으로 가입한 경우 Google 로그인을 이용해주세요.' : 'This email is already registered. If you signed up with Google, please use Google login.');
      } else if (code === 'auth/invalid-email') {
        setError(isKo ? '유효하지 않은 이메일 형식입니다.' : 'Invalid email format.');
      } else if (code === 'auth/weak-password') {
        setError(isKo ? '비밀번호가 너무 약합니다. 6자 이상 입력해주세요.' : 'Password is too weak. Use at least 6 characters.');
      } else {
        setError(isKo ? '회원가입에 실패했습니다.' : 'Sign up failed.');
      }
    }
  };

  const handleGoogleSignup = async () => {
    if (!agreed || !privacyAgreed) {
      setError(isKo ? '서비스 이용약관과 개인정보처리방침에 동의해주세요.' : 'Please agree to the Terms of Service and Privacy Policy.');
      return;
    }
    setError('');
    try {
      await googleSignIn(true);
    } catch (err: any) {
      const msg = err.message ?? '';
      if (msg.startsWith('COOLDOWN:')) {
        const days = msg.split(':')[1];
        setError(t('auth.deletionCooldown', { days }));
      } else if (msg.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER')) {
        const existingEmail = msg.split(':')[1] ?? '';
        const shown = existingEmail ? `(${existingEmail}) ` : '';
        setError(isKo
          ? `이 이메일 ${shown}은(는) 이미 다른 방식(이메일/비밀번호 또는 Apple)으로 가입되어 있습니다. 해당 방식으로 먼저 로그인한 뒤, 이메일 인증을 완료해 주세요. 그 후에는 Google 로그인도 같은 계정에 자동으로 연결됩니다.`
          : `This email ${shown}is already registered with a different sign-in method (email/password or Apple). Please sign in with that method first and verify your email — Google sign-in will then be linked automatically to the same account.`);
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError(isKo ? 'Google 가입에 실패했습니다.' : 'Google signup failed.');
      }
    }
  };

  const handleAppleSignup = async () => {
    if (!agreed || !privacyAgreed) {
      setError(isKo ? '서비스 이용약관과 개인정보처리방침에 동의해주세요.' : 'Please agree to the Terms of Service and Privacy Policy.');
      return;
    }
    setError('');
    try {
      await appleSignIn(true);
    } catch (err: any) {
      const msg = err.message ?? '';
      if (msg.startsWith('COOLDOWN:')) {
        const days = msg.split(':')[1];
        setError(t('auth.deletionCooldown', { days }));
      } else if (msg.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER')) {
        const existingEmail = msg.split(':')[1] ?? '';
        const shown = existingEmail ? `(${existingEmail}) ` : '';
        setError(isKo
          ? `이 이메일 ${shown}은(는) 이미 다른 방식(이메일/비밀번호 또는 Google)으로 가입되어 있습니다. 해당 방식으로 먼저 로그인한 뒤, 이메일 인증을 완료해 주세요. 그 후에는 Apple 로그인도 같은 계정에 자동으로 연결됩니다.`
          : `This email ${shown}is already registered with a different sign-in method (email/password or Google). Please sign in with that method first and verify your email — Apple sign-in will then be linked automatically to the same account.`);
      } else if (msg === 'cancelled' || msg.includes('popup-closed') || msg.includes('canceled') || msg.includes('cancel')) {
        // User cancelled — silent
      } else {
        const detail = msg.replace(/^Apple Sign-In failed:\s*/, '');
        setError(isKo ? `Apple 가입 실패: ${detail}` : `Apple signup failed: ${detail}`);
      }
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-6 py-6"
      style={{ backgroundColor: colors.background }}>

      {/* Logo + description */}
      <h1 className="mb-1 text-4xl font-bold" style={{
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        {isKo ? '우리들의 주파수' : 'OurHertz'}
      </h1>
      <p className="mb-2 text-sm font-semibold" style={{ color: colors.textSecondary }}>{isKo ? 'OurHertz' : t('app.name')}</p>
      <p className="mb-1 max-w-[340px] text-center text-xs leading-relaxed" style={{ color: colors.textMuted, wordBreak: 'keep-all' }}>
        {isKo
          ? 'GPS 기반 무전기 채팅 서비스. 내 위치가 주파수가 되어 주변 사람들과 실시간으로 소통하세요.'
          : 'GPS-based walkie-talkie chat. Your location becomes your frequency — connect with nearby people in real time.'}
      </p>
      <div className="mb-5 flex items-center gap-3 text-[10px]" style={{ color: colors.textMuted }}>
        <span>📡 {isKo ? '실시간 채팅' : 'Live Chat'}</span>
        <span>📍 {isKo ? '위치 메시지 드롭' : 'Location Drops'}</span>
        <span>🗺️ {isKo ? '레이더 지도' : 'Radar Map'}</span>
      </div>

      {/* Signup card */}
      <div className="w-full max-w-[360px] rounded-xl p-5"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          <input type="email" placeholder={t('auth.email')} value={email}
            onChange={(e) => setEmail(e.target.value)} required
            className="w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none"
            style={{ borderColor: colors.border, color: colors.text }} />

          <div className="relative">
            <input type={showPwd ? 'text' : 'password'} placeholder={t('auth.password')}
              value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full rounded-lg border bg-transparent px-3 py-2.5 pr-10 text-sm outline-none"
              style={{ borderColor: colors.border, color: colors.text }} />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: colors.textMuted }}>
              {showPwd ? '🙈' : '👁'}
            </button>
          </div>

          <input type="password" placeholder={t('auth.passwordConfirm')}
            value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} required
            className="w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none"
            style={{ borderColor: colors.border, color: colors.text }} />

          <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: colors.textSecondary }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-[#4CAF50]" />
            <span>
              <a href={isKo ? '/terms_ko.html' : '/terms_en.html'} target="_blank" rel="noopener" style={{ color: colors.primary, textDecoration: 'underline' }}>
                {isKo ? '서비스 이용약관' : 'Terms of Service'}
              </a>
              {isKo ? '에 동의합니다' : ' agreed'}
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: colors.textSecondary }}>
            <input type="checkbox" checked={privacyAgreed} onChange={(e) => setPrivacyAgreed(e.target.checked)}
              className="mt-0.5 accent-[#4CAF50]" />
            <span>
              <a href={isKo ? '/privacy_ko.html' : '/privacy_en.html'} target="_blank" rel="noopener" style={{ color: colors.primary, textDecoration: 'underline' }}>
                {isKo ? '개인정보처리방침' : 'Privacy Policy'}
              </a>
              {isKo ? '에 동의합니다' : ' agreed'}
            </span>
          </label>

          {error && <p className="text-center text-xs" style={{ color: colors.error }}>{error}</p>}

          <button type="submit" disabled={loading || !agreed || !privacyAgreed || !email || !password || !passwordConfirm}
            className="w-full rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: colors.primary }}>
            {loading ? t('common.loading') : t('auth.signup')}
          </button>
        </form>

        {/* Divider */}
        <div className="my-3 flex items-center gap-3">
          <div className="flex-1 border-t" style={{ borderColor: colors.border }} />
          <span className="text-xs" style={{ color: colors.textMuted }}>{t('auth2.or')}</span>
          <div className="flex-1 border-t" style={{ borderColor: colors.border }} />
        </div>

        {/* Google signup */}
        <button onClick={handleGoogleSignup} disabled={loading || !agreed || !privacyAgreed}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold disabled:opacity-40"
          style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>
          <GoogleIcon />
          {t('auth2.googleSignup')}
        </button>

        {/* Apple signup */}
        <button onClick={handleAppleSignup} disabled={loading || !agreed || !privacyAgreed}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold disabled:opacity-40"
          style={{ backgroundColor: '#000', color: '#fff', border: `1px solid ${colors.border}` }}>
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.784 9.484c-.023-2.392 1.952-3.541 2.04-3.597-1.11-1.624-2.838-1.846-3.454-1.872-1.47-.149-2.87.866-3.616.866-.745 0-1.897-.844-3.116-.822-1.604.024-3.082.933-3.908 2.37-1.666 2.89-.426 7.17 1.197 9.516.794 1.147 1.74 2.436 2.982 2.39 1.195-.048 1.647-.774 3.092-.774 1.444 0 1.848.774 3.11.75 1.287-.022 2.103-1.17 2.892-2.32.912-1.331 1.287-2.62 1.31-2.687-.028-.013-2.513-.965-2.538-3.83l.01.01zM11.39 2.632c.66-.8 1.105-1.91.984-3.017-.951.039-2.103.615-2.78 1.395-.607.7-1.14 1.82-.996 2.894 1.062.083 2.147-.523 2.792-1.272z" fill="currentColor"/>
          </svg>
          {t('auth2.appleSignup')}
        </button>
        {(!agreed || !privacyAgreed) && (
          <p className="mt-1 text-center text-[10px]" style={{ color: colors.textMuted }}>
            {isKo ? '* 약관 동의가 필요합니다' : '* Agreement to terms is required'}
          </p>
        )}

        {/* Link to login */}
        <div className="mt-4 text-center text-xs">
          <span style={{ color: colors.textMuted }}>{isKo ? '이미 계정이 있으신가요?' : 'Already have an account?'} </span>
          <Link to="/login" className="font-semibold" style={{ color: colors.primary }}>
            {isKo ? '로그인' : 'Login'}
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 max-w-[360px] text-center text-[10px] leading-relaxed" style={{ color: colors.textMuted }}>
        <div className="mb-1.5 flex items-center justify-center gap-2">
          <a href={isKo ? '/terms_ko.html' : '/terms_en.html'} target="_blank" rel="noopener"
            style={{ color: colors.textSecondary, textDecoration: 'underline' }}>
            {isKo ? '서비스 이용약관' : 'Terms of Service'}
          </a>
          <span>|</span>
          <a href={isKo ? '/privacy_ko.html' : '/privacy_en.html'} target="_blank" rel="noopener"
            style={{ color: colors.textSecondary, textDecoration: 'underline' }}>
            {isKo ? '개인정보처리방침' : 'Privacy Policy'}
          </a>
        </div>
        <p>&copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{isKo ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.</p>
        <p className="mt-1">Web v{__APP_VERSION__}</p>
      </div>

      {!Capacitor.isNativePlatform() && (
        <Link to="/" className="mt-3 text-xs" style={{ color: colors.primary }}>
          ← {isKo ? '홈으로' : 'Back to Home'}
        </Link>
      )}
    </div>
  );
}
